/**
 * Feb 2026 · P1 — Admin Test Catalog Pricing
 *
 * Renders /admin/pricing/test-catalog: one row per shipped water test
 * package (AWARE 4-Point / Tank 360 / Bore 360). Inline edit lets an
 * admin override retail and online-prepaid price without a code deploy.
 *
 * Behaviour:
 *  - Each row shows the CURRENT effective price (default vs override)
 *  - Editing retail alone auto-recomputes online-prepaid on the backend
 *  - "Reset to default" removes the override + restores the built-in
 *    price. History is archived to `test_catalog_override_archive`.
 *  - Member price mirrors online-prepaid unless explicitly overridden.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, RotateCcw, Save, Pencil, X, Beaker,
  Loader2, AlertTriangle, CheckCircle2, RefreshCw,
} from "lucide-react";
import { formatDateTimeAU } from "@/lib/formatDateAU";

const fmt = (v) =>
  v == null || v === ""
    ? "—"
    : Number(v).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

export default function AdminTestCatalogPricing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // { test_id }
  const [draft, setDraft] = useState({ retail: "", online_prepaid: "", member: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null); // test_id being reset

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/admin/pricing/test-catalog");
      setData(res.data);
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't load test-catalog pricing.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (row) => {
    setEditing({ test_id: row.test_id, name: row.name });
    setDraft({
      // Prefill with effective values so admins can see + tweak the current numbers.
      retail:         String(row.effective_retail ?? ""),
      online_prepaid: String(row.effective_online_prepaid ?? ""),
      member:         String(row.effective_member ?? ""),
      note:           "",
    });
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraft({ retail: "", online_prepaid: "", member: "", note: "" });
  };

  const save = async () => {
    if (!editing) return;
    // Only send the fields that differ from the effective values so
    // the backend can leave the rest at their defaults. Empty inputs
    // → null (no override on that field).
    const asNum = (v) => (v === "" || v == null ? null : Number(v));
    const payload = {
      retail:         asNum(draft.retail),
      online_prepaid: asNum(draft.online_prepaid),
      member:         asNum(draft.member),
      note:           draft.note.trim() || null,
    };
    for (const k of ["retail", "online_prepaid", "member"]) {
      if (payload[k] != null && (isNaN(payload[k]) || payload[k] < 0)) {
        toast.error(`Please enter a valid non-negative number for ${k.replace("_", " ")}.`);
        return;
      }
    }
    if (payload.retail == null && payload.online_prepaid == null && payload.member == null) {
      toast.error("Enter at least one price to override.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/pricing/test-catalog/${editing.test_id}`, payload);
      toast.success(`${editing.name} pricing updated — live immediately.`);
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const resetRow = async (row) => {
    if (!window.confirm(
      `Reset ${row.name} to the built-in default price (${fmt(row.default_retail)} retail / ${fmt(row.default_online_prepaid)} online prepaid)?\n\nThe existing override will be archived — no data lost.`,
    )) return;
    setResetting(row.test_id);
    try {
      await api.post(`/admin/pricing/test-catalog/${row.test_id}/reset`);
      toast.success(`${row.name} reset to default.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Reset failed.");
    } finally {
      setResetting(null);
    }
  };

  return (
    <PortalShell title="Test Catalog Pricing" subtitle="Admin · Pricing">
      <div className="flex items-center justify-between mb-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="test-catalog-pricing-back">
          <ArrowLeft className="w-4 h-4" /> Admin
        </Link>
        <Button
          variant="outline" onClick={load} data-testid="test-catalog-pricing-refresh"
          className="border-slate-300"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          <span className="ml-1.5 text-sm">Refresh</span>
        </Button>
      </div>

      <div className="rounded-2xl border border-cyan-100 bg-cyan-50/60 p-4 mb-6 flex items-start gap-3">
        <Beaker className="w-5 h-5 text-cyan-700 shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700 leading-relaxed">
          <div className="font-semibold text-slate-900">Public retail + online-prepaid prices for the shipped AWARE tests.</div>
          <div className="mt-1 text-slate-600">
            Overrides here apply IMMEDIATELY to every downstream reader — booking flow, admin quotes, Stripe checkout, PDFs, partner portal, wholesale bundles. No redeploy needed. Members and online-prepaid customers keep their {data ? `${data.online_prepaid_discount_pct}%` : "20%"} discount unless you override online-prepaid explicitly.
          </div>
        </div>
      </div>

      {err && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> {err}
        </div>
      )}

      {loading && !data ? (
        <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="test-catalog-pricing-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <th className="text-left px-4 py-3">Test</th>
                <th className="text-right px-4 py-3">Retail</th>
                <th className="text-right px-4 py-3">Online prepaid</th>
                <th className="text-right px-4 py-3">Member</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.rows || []).map((row) => (
                <tr key={row.test_id} data-testid={`test-catalog-row-${row.test_id}`} className="align-top">
                  <td className="px-4 py-4">
                    <div className="font-semibold text-slate-900">{row.name}</div>
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5">{row.test_id}</div>
                    {row.override_updated_at && (
                      <div className="text-[11px] text-amber-700 mt-1">
                        Overridden {formatDateTimeAU(row.override_updated_at)} · by {row.override_updated_by || "system"}
                        {row.override_note && <div className="italic text-slate-500 mt-0.5">"{row.override_note}"</div>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="font-bold text-slate-900 tabular-nums" data-testid={`test-catalog-${row.test_id}-effective-retail`}>{fmt(row.effective_retail)}</div>
                    {row.is_overridden && row.effective_retail !== row.default_retail && (
                      <div className="text-[10px] text-slate-400 line-through tabular-nums">{fmt(row.default_retail)}</div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="font-bold text-cyan-700 tabular-nums" data-testid={`test-catalog-${row.test_id}-effective-online`}>{fmt(row.effective_online_prepaid)}</div>
                    {row.is_overridden && row.effective_online_prepaid !== row.default_online_prepaid && (
                      <div className="text-[10px] text-slate-400 line-through tabular-nums">{fmt(row.default_online_prepaid)}</div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="font-semibold text-emerald-700 tabular-nums" data-testid={`test-catalog-${row.test_id}-effective-member`}>{fmt(row.effective_member)}</div>
                  </td>
                  <td className="px-4 py-4">
                    {row.is_overridden ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border border-amber-200 bg-amber-50 text-amber-700">
                        <Pencil className="w-3 h-3" /> Overridden
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border border-slate-200 bg-slate-50 text-slate-600">
                        <CheckCircle2 className="w-3 h-3" /> Default
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => startEdit(row)}
                        data-testid={`test-catalog-${row.test_id}-edit`}
                        className="h-8 px-3 rounded-lg border-slate-300 text-slate-700"
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      {row.is_overridden && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => resetRow(row)}
                          disabled={resetting === row.test_id}
                          data-testid={`test-catalog-${row.test_id}-reset`}
                          className="h-8 px-3 rounded-lg border-slate-300 text-slate-600 hover:text-slate-900"
                        >
                          {resetting === row.test_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          <span className="ml-1">Reset</span>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div
          data-testid="test-catalog-pricing-edit-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          onClick={cancelEdit}
        >
          <div
            className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-700">Edit pricing</div>
                <h3 className="text-lg font-bold text-slate-900 mt-0.5">{editing.name}</h3>
              </div>
              <button onClick={cancelEdit} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="test-catalog-edit-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">Retail price (AUD)</label>
                <Input
                  type="number" step="0.01" min="0"
                  value={draft.retail}
                  data-testid="test-catalog-edit-retail"
                  onChange={(e) => setDraft({ ...draft, retail: e.target.value })}
                  className="mt-1"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Leaving this blank keeps the built-in retail price. Changing it auto-recomputes online prepaid at {data?.online_prepaid_discount_pct ?? 20}% off unless you override that too.
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Online prepaid price (AUD)</label>
                <Input
                  type="number" step="0.01" min="0"
                  value={draft.online_prepaid}
                  data-testid="test-catalog-edit-online"
                  onChange={(e) => setDraft({ ...draft, online_prepaid: e.target.value })}
                  className="mt-1"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Leave blank to auto-derive from retail. Set explicitly to lock a non-standard discount.
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Member price (AUD)</label>
                <Input
                  type="number" step="0.01" min="0"
                  value={draft.member}
                  data-testid="test-catalog-edit-member"
                  onChange={(e) => setDraft({ ...draft, member: e.target.value })}
                  className="mt-1"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Members currently pay the same as online prepaid. Only set this if you want to reintroduce a member-only discount.
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Note <span className="font-normal text-slate-400">(optional, for audit)</span></label>
                <Textarea
                  value={draft.note}
                  data-testid="test-catalog-edit-note"
                  rows={2} maxLength={400}
                  onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                  className="mt-1"
                  placeholder="e.g. Q3 price rise · effective 1 Aug"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <Button variant="outline" onClick={cancelEdit} data-testid="test-catalog-edit-cancel" className="border-slate-300 text-slate-700">
                Cancel
              </Button>
              <Button onClick={save} disabled={saving} data-testid="test-catalog-edit-save" className="bg-cyan-600 hover:bg-cyan-700 text-white">
                {saving ? (<><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</>) : (<><Save className="w-3.5 h-3.5 mr-1.5" /> Save override</>)}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PortalShell>
  );
}
